import React, { Component } from 'react';
import { connect } from 'react-redux';

class PremiumUpgradeScreen extends Component {
    constructor(props) {
        super(props);

        this.state = {
            progress: 0,
            loadingStartTime: Date.now(),
            loadingPhase: 0,
            currentTask: 0,
            processingPaused: false
        };

        // Loading phases with different speeds and pauses
        this.loadingPhases = [
            { duration: 2500, target: 15 },   // Initial progress
            { duration: 1500, target: 15 },   // Pause at 15%
            { duration: 2000, target: 30 },   // More progress
            { duration: 1500, target: 30 },   // Pause at 30%
            { duration: 2000, target: 45 },   // Progress continues
            { duration: 1000, target: 45 },   // Brief pause at 45%
            { duration: 2500, target: 60 },   // More progress
            { duration: 1500, target: 60 },   // Pause at 60%
            { duration: 2000, target: 75 },   // Moving along
            { duration: 2000, target: 75 },   // Pause at 75%
            { duration: 2000, target: 90 },   // Near completion
            { duration: 1000, target: 90 },   // Short pause at 90%
            { duration: 2500, target: 100 },  // Final push to completion
        ];

        // Loading tasks to display at certain percentage points
        this.loadingTasks = [
            { threshold: 0, message: "Initializing premium servers..." },
            { threshold: 5, message: "Configuring unlimited servers capability..." },
            { threshold: 10, message: "Removing server limits from your account..." },
            { threshold: 15, message: "Enabling unlimited custom commands..." },
            { threshold: 20, message: "Enabling unlimited custom events..." },
            { threshold: 25, message: "Setting up custom status capability..." },
            { threshold: 30, message: "Removing BotGhost branding..." },
            { threshold: 35, message: "Configuring priority support access..." },
            { threshold: 40, message: "Unlocking premium modules..." },
            { threshold: 45, message: "Enhancing server performance..." },
            { threshold: 50, message: "Applying premium account badge..." },
            { threshold: 55, message: "Allocating dedicated server resources..." },
            { threshold: 60, message: "Configuring premium templates..." },
            { threshold: 65, message: "Setting up unlimited storage capability..." },
            { threshold: 70, message: "Applying premium Discord role..." },
            { threshold: 75, message: "Upgrading data storage limits..." },
            { threshold: 80, message: "Finalizing premium account privileges..." },
            { threshold: 85, message: "Running performance optimizations..." },
            { threshold: 90, message: "Performing final system checks..." },
            { threshold: 95, message: "Finalizing your premium upgrade..." }
        ];

        // Total estimated loading time around 22 seconds
        this.totalDuration = this.loadingPhases.reduce((sum, phase) => sum + phase.duration, 0);
    }

    componentDidMount() {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera

        this.processNextPhase();
    }

    processNextPhase = () => {
        const { loadingPhase } = this.state;

        // If we've completed all phases, redirect to Stripe checkout
        if (loadingPhase >= this.loadingPhases.length) {
            // Get the stored checkout URL from sessionStorage
            const checkoutUrl = sessionStorage.getItem('checkout_redirect_url');

            if (checkoutUrl) {
                // Clear the stored URL
                sessionStorage.removeItem('checkout_redirect_url');
                // Redirect to Stripe checkout
                window.location.href = checkoutUrl;
            } else {
                // Fallback if no URL is stored
                window.location.href = "/dashboard";
            }
            return;
        }

        const currentPhase = this.loadingPhases[loadingPhase];
        const startProgress = loadingPhase === 0 ? 0 : this.loadingPhases[loadingPhase - 1].target;
        const targetProgress = currentPhase.target;
        const phaseDuration = currentPhase.duration;

        // For even-indexed phases, progress the loading bar
        // For odd-indexed phases, simulate "processing" by pausing progress
        const isProgressPhase = loadingPhase % 2 === 0;

        if (isProgressPhase) {
            // Animated progress
            const startTime = Date.now();

            this.progressInterval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const phaseProgress = Math.min(1, elapsed / phaseDuration);

                // Use easeInOut for smoother animation
                const easedProgress = this.easeInOutQuad(phaseProgress);
                const newProgress = startProgress + (targetProgress - startProgress) * easedProgress;

                // Update current task based on progress
                const newTask = this.determineCurrentTask(newProgress);

                this.setState({
                    progress: Math.round(newProgress),
                    currentTask: newTask
                });

                if (phaseProgress >= 1) {
                    clearInterval(this.progressInterval);
                    this.setState({
                        loadingPhase: loadingPhase + 1,
                        processingPaused: !isProgressPhase
                    }, this.processNextPhase);
                }
            }, 50);
        } else {
            // Simulate processing by pausing the progress bar
            this.setState({ processingPaused: true });

            setTimeout(() => {
                this.setState({
                    loadingPhase: loadingPhase + 1,
                    processingPaused: false
                }, this.processNextPhase);
            }, phaseDuration);
        }
    };

    // Easing function for smoother animation
    easeInOutQuad = (t) => {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    };

    determineCurrentTask = (progress) => {
        // Find the highest threshold that is less than or equal to the current progress
        let taskIndex = 0;
        for (let i = 0; i < this.loadingTasks.length; i++) {
            if (progress >= this.loadingTasks[i].threshold) {
                taskIndex = i;
            } else {
                break;
            }
        }
        return taskIndex;
    };

    componentWillUnmount() {
        // Clear intervals to prevent memory leaks
        clearInterval(this.progressInterval);
    }

    getCurrentLoadingMessage() {
        return this.loadingTasks[this.state.currentTask].message;
    }

    render() {
        return (
            <div className="min-h-screen bg-[#111111] flex flex-col">
                {/* Header with logo */}
                <div className="bg-[#1A1A2E] py-6 border-b border-[#FF4444]">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="flex justify-center">
                            <img src={process.env.PUBLIC_URL + '/images/logo-red.png'} alt="BotGhost Logo" className="h-10" />
                        </div>
                    </div>
                </div>

                {/* Main content */}
                <div className="flex-grow flex items-center justify-center px-4 py-8">
                    <div className="w-full max-w-6xl mx-auto">
                        {/* Main container */}
                        <div className="bg-gradient-to-br from-[#1A1A2E] to-[#2A1A1A] rounded-xl p-8 border border-[#FF4444] shadow-lg">
                            {/* Loading status section */}
                            <div className="mb-10 text-center">
                                <div className="inline-block bg-[#FFD700] text-black px-4 py-2 rounded-full text-sm font-bold mb-4">
                                    PREMIUM UPGRADE PROCESSING
                                </div>
                                <h2 className="text-4xl font-bold text-white mb-4">
                                    Setting Up Your Premium Features
                                </h2>
                                <p className="text-[#FF4444] text-xl">
                                    Please wait while we upgrade your bot with premium features and functionality
                                </p>
                            </div>

                            {/* Main image */}
                            <div className="mb-10 flex justify-center">
                                <div className="relative">
                                    <div className={`overflow-hidden shadow-xl ${this.state.processingPaused ? 'animate-pulse' : ''}`}>
                                        <div className="w-64 h-64 flex items-center justify-center bg-gradient-to-r from-[#1a1a2e] to-[#2a1a1a] rounded-full border-4 border-[#FFD700]">
                                            <div className="text-[#FFD700] text-9xl">👑</div>
                                        </div>
                                    </div>

                                    {/* Processing indicator */}
                                    {this.state.processingPaused && (
                                        <div className="absolute top-4 right-4 bg-[#FFD700] text-black px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                                            PROCESSING
                                        </div>
                                    )}

                                    {/* Floating stars decoration */}
                                    <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                                        <div className="absolute top-2 right-[5%] text-[#FFD700] text-3xl animate-pulse delay-100">✦</div>
                                        <div className="absolute top-16 right-[15%] text-[#FFD700] text-2xl animate-pulse delay-300">✧</div>
                                        <div className="absolute top-4 right-[25%] text-[#FFD700] text-3xl animate-pulse delay-500">✦</div>
                                        <div className="absolute bottom-20 left-[15%] text-[#FFD700] text-2xl animate-pulse delay-700">✧</div>
                                    </div>
                                </div>
                            </div>

                            {/* Loading message */}
                            <div className="mb-8 text-center">
                                <p className="text-2xl text-white font-medium">
                                    {this.getCurrentLoadingMessage()}
                                </p>
                            </div>

                            {/* Progress bar */}
                            <div className="mb-8">
                                <div className="w-full h-6 bg-[#1E1E30] rounded-full overflow-hidden">
                                    <div
                                        className={`h-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] transition-all duration-200 ease-out ${this.state.processingPaused ? 'animate-pulse' : ''}`}
                                        style={{ width: `${this.state.progress}%` }}
                                    />
                                </div>
                                <div className="flex justify-between mt-2 text-sm">
                                    <span className="text-[#FFD700]">Starting upgrade</span>
                                    <span className="text-white font-medium">{this.state.progress}%</span>
                                    <span className="text-[#FFD700]">Completion</span>
                                </div>
                            </div>

                            {/* Info message */}
                            <div className="text-center text-[#FF4444] text-sm">
                                <p>Your premium features are being activated. This process will take just a few moments.</p>
                                <p className="mt-1">You will be automatically redirected to your dashboard when the process is complete.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-[#1A1A2E] py-4 border-t border-[#FF4444]">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center text-[#FF4444] text-sm">
                            © {new Date().getFullYear()} BotGhost - Premium
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    user: state.data.user,
    auth: state.auth
});

export default connect(mapStateToProps)(PremiumUpgradeScreen); 